import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase/supabase';

const API_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL || '';
const APP_LOGIN_URL = process.env.REACT_APP_APP_HOME_URL
  ? `${process.env.REACT_APP_APP_HOME_URL}/login`
  : 'https://app.pedagogue.io/login';

/**
 * Intercepts ?handoff_token= query param, redeems via backend,
 * and establishes a Supabase session before rendering children.
 *
 * CRITICAL: Children are gated (not rendered) while a handoff token
 * is present. Without this gate, ProtectedRoute renders on the first
 * frame with user=null and redirects to auto-handoff before the async
 * redeem can complete.
 */
const HandoffHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const handoffToken = searchParams.get('handoff_token');

  // Block children from the FIRST render when a handoff token is present.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [processing, _setProcessing] = useState(!!handoffToken);
  const processedRef = useRef(false);

  useEffect(() => {
    if (!handoffToken || processedRef.current) return;
    processedRef.current = true;

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/handoff/redeem`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Handoff-Source': 'handler',
          },
          body: JSON.stringify({ token: handoffToken }),
        });

        if (!res.ok) {
          console.warn('[Handoff] Redemption returned', res.status);

          // If 403 (already redeemed), a prefetch or duplicate request may have
          // already established a valid session in localStorage. Check before
          // giving up and redirecting to login.
          if (res.status === 403) {
            const { data: { session: existingSession } } = await supabase.auth.getSession();
            if (existingSession?.access_token) {
              console.log('[Handoff] Session already established. Proceeding.');
              const url = new URL(window.location.href);
              url.searchParams.delete('handoff_token');
              url.searchParams.delete('handoff_attempted');
              window.location.replace(url.toString());
              return;
            }
          }

          console.error('[Handoff] No session found after failed redemption. Redirecting to login.');
          window.location.href = APP_LOGIN_URL;
          return;
        }

        const data = await res.json();

        // If the backend returned session credentials, establish a Supabase session
        if (data.session?.access_token && data.session?.refresh_token) {
          await supabase.auth.setSession({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          });
        } else {
          console.error('[Handoff] No session credentials in redeem response');
          window.location.href = APP_LOGIN_URL;
          return;
        }

        // Full page reload so AuthProvider picks up the newly-stored session.
        // Client-side navigate() is insufficient — ProtectedRoute may re-render
        // before onAuthStateChange fires.
        const url = new URL(window.location.href);
        url.searchParams.delete('handoff_token');
        url.searchParams.delete('handoff_attempted');
        window.location.replace(url.toString());
      } catch (err) {
        console.error('[Handoff] Error:', err);
        window.location.href = APP_LOGIN_URL;
      }
    })();
  }, [handoffToken]); // eslint-disable-line react-hooks/exhaustive-deps

  if (processing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <span>Establishing session...</span>
      </div>
    );
  }

  return <>{children}</>;
};

export default HandoffHandler;
